<template>
  <div class="consumer-mypage-order" v-if="userData">
    <div class="main-container">
      <div class="menu-container">
        <div>
          <h3>나의 쇼핑 정보</h3>
          <ul>
            <li>
              <router-link to="/mypage/order">주문내역 조회</router-link>
            </li>
          </ul>
        </div>
        <div>
          <h3>활동 정보</h3>
          <ul>
            <li>
              <router-link to="/mypage/wishlist">나의 찜</router-link>
            </li>
          </ul>
        </div>
        <div>
          <h3>나의 정보</h3>
          <ul>
            <li>
              <router-link to="/mypage/edit">회원 정보 수정</router-link>
            </li>
            <li>
              <button @click="resetPassword">비밀번호 재설정</button>
            </li>
            <li>
              <button @click="logout">로그아웃</button>
            </li>
          </ul>
        </div>
      </div>
      <div class="order-container">
        <div>
          <h3>주문내역 조회</h3>
          <div class="order-filter-container">
            <select v-model="orderStatusFilter">
              <option value="">전체 주문처리상태</option>
              <option value="BEFORE_PAYMENT">결제전</option>
              <option value="PAYMENT_COMPLETED">결제완료</option>
              <option value="PREPARING_PRODUCT">상품준비중</option>
              <option value="PREPARING_DELIVERY">배송준비중</option>
              <option value="SHIPPING_PROGRESS">배송중</option>
              <option value="DELIVERY_COMPLETED">배송완료</option>
              <option value="CANCELLED">취소</option>
              <option value="EXCHANGE">교환</option>
              <option value="RETURNED">반품</option>
            </select>
            <div class="date-button-container">
              <input
                type="radio"
                v-model="dateRangeButton"
                id="TODAY"
                value="TODAY"
                style="display: none"
              />
              <label for="TODAY">오늘</label>
              <input
                type="radio"
                v-model="dateRangeButton"
                id="1M"
                value="1M"
                style="display: none"
              />
              <label for="1M">1개월</label>
              <input
                type="radio"
                v-model="dateRangeButton"
                id="3M"
                value="3M"
                style="display: none"
              />
              <label for="3M">3개월</label>
              <input
                type="radio"
                v-model="dateRangeButton"
                id="6M"
                value="6M"
                style="display: none"
              />
              <label for="6M">6개월</label>
            </div>
            <div class="date-range-container">
              <input type="date" v-model="startDate" :max="endDate" />
              <p>~</p>
              <input type="date" v-model="endDate" disabled />
            </div>
            <button @click="applyFilters">조회</button>
          </div>
          <div class="order-list-container" v-if="orderData.length > 0">
            <div
              class="order-item"
              v-for="(order, index) in filteredOrderData"
              :key="order.id"
            >
              <div class="order-title-container">
                <strong
                  >{{ formatDate(order.createdAt.toDate())
                  }}<span>({{ order.orderId }})</span></strong
                >
                <router-link :to="`/mypage/order/detail?id=${order.orderId}`">
                  <p>상세보기</p>
                  <span class="material-icons-outlined"> chevron_right </span>
                </router-link>
              </div>
              <div
                class="order-product-container"
                v-for="(
                  productOrderItem, productOrderIndex
                ) in order.productOrderDatas"
                :key="productOrderIndex"
              >
                <div class="desc-container">
                  <img
                    :src="
                      productOrderItem.productData.productThumbnailUrl
                        .originalUrl
                    "
                  />
                  <div>
                    <p class="name">
                      {{ productOrderItem.productData.productName }}
                    </p>
                    <p class="price">
                      {{ productOrderItem.productPrice.toLocaleString() }}원 ({{
                        productOrderItem.count
                      }}개)
                    </p>
                    <p class="option">
                      [옵션: {{ productOrderItem.optionName }}]
                    </p>
                  </div>
                </div>
                <hr />
                <div class="order-status-container">
                  <strong>{{
                    generateOrderStatusLabel(productOrderItem.status)
                  }}</strong>
                  <hr
                    v-if="
                      productOrderIndex !== order.productOrderDatas.length - 1
                    "
                  />
                  <div v-else class="order-total-container">
                    <p>
                      총 결제금액:
                      <span> {{ order.totalPrice.toLocaleString() }}원 </span>
                    </p>
                    <strong
                      v-if="
                        order.deliveryFeePaymentRequired &&
                        !order.deliveryFeePaied
                      "
                    >
                      "상세보기"에서 배송비 추가 결제가 필요합니다!
                    </strong>
                  </div>
                </div>
                <hr v-if="index === orderData.length - 1" />
              </div>
            </div>
          </div>
          <div class="order-empty-container" v-else>
            <span class="material-icons-outlined"> error_outline </span>
            <p>주문 내역이 없습니다.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="js">
import { getUserId, logoutProcess } from '@/lib/auth';
import { db } from '@/lib/firebase';
import { generateOrderStatusLabel } from '@/lib/utils';
import { doc, getDoc, getDocs, query, collection, where, orderBy, Timestamp } from 'firebase/firestore';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useBusyLock } from "@/lib/useBusyLock";

const { runLocked } = useBusyLock();

const userData = ref(null);
const orderData = ref([]);
const orderStatusFilter = ref("");
const dateRangeButton = ref("3M"); // 기본값
const startDate = ref("");
const endDate = ref("");

const filteredOrderData = computed(() => {
  return orderData.value.filter((order) => order.productOrderDatas?.length > 0);
});

const router = useRouter();
const route = useRoute();

function formatDate(date) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const resetPassword = async () => {
  router.push("/mypage/reset-password");
}

const logout = async () => {
  await logoutProcess();
  window.location.href = "/";
}

const toDateInputFormat = (date) => date.toISOString().split("T")[0];

const applyFilters = async () => {
  router.replace({
    query: {
      filter: orderStatusFilter.value,
      startDate: startDate.value,
      endDate: endDate.value,
    },
  });
};

const fetchOrdersCore = async () => {
  const uid = getUserId();

  const filters = [
    where("orderChannel", "==", "NECOKUMO"),
    where("userId", "==", uid),
  ];

  const start = new Date(startDate.value);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate.value);
  end.setHours(23, 59, 59, 999);

  filters.push(where("createdAt", ">=", Timestamp.fromDate(start)));
  filters.push(where("createdAt", "<=", Timestamp.fromDate(end)));

  const q = query(
    collection(db, "order"),
    ...filters,
    orderBy("createdAt", "desc")
  );

  const orderSnap = await getDocs(q);

  // id도 같이 넣어두면 나중에 편함
  orderData.value = orderSnap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));

  for (const order of orderData.value) {
    order.productOrderDatas = [];

    for (const productOrderId of order.productOrders || []) {
      const productOrderSnap = await getDoc(doc(db, "productOrder", productOrderId));
      if (!productOrderSnap.exists()) continue;

      const productOrderData = productOrderSnap.data();

      // 상태 필터링
      if (
        orderStatusFilter.value !== "" &&
        productOrderData.status !== orderStatusFilter.value
      ) {
        continue;
      }

      const productSnap = await getDoc(doc(db, "product", productOrderData.productId));

      order.productOrderDatas.push({
        ...productOrderData,
        productData: productSnap.exists() ? productSnap.data() : null,
      });
    }
  }

  // (선택) 상태 필터 적용 후 비어버린 주문 숨기고 싶으면 아래 사용
  // if (orderStatusFilter.value !== "") {
  //   orderData.value = orderData.value.filter(
  //     (order) => (order.productOrderDatas || []).length > 0
  //   );
  // }
};

// 외부에서 호출하는 함수는 runLocked 래핑
const fetchOrders = () =>
  runLocked(fetchOrdersCore).catch((error) => {
    console.error("주문 목록 조회 실패:", error);
    alert("주문 목록을 불러오는 중 오류가 발생했습니다.");
  });

onMounted(() => {
  runLocked(async () => {
    const uid = getUserId();

    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    threeMonthsAgo.setHours(0, 0, 0, 0);

    const queryFilter = route.query.filter;
    const queryStart = route.query.startDate;
    const queryEnd = route.query.endDate;

    startDate.value = queryStart || toDateInputFormat(threeMonthsAgo);
    endDate.value = queryEnd || toDateInputFormat(today);
    orderStatusFilter.value = queryFilter || "";

    const userSnap = await getDoc(doc(db, "users", uid));
    userData.value = userSnap.exists() ? userSnap.data() : null;

    await fetchOrdersCore();
  }).catch((error) => {
    console.error(error);
    alert("주문 페이지 초기화 중 오류가 발생했습니다.");
  });
});

watch(dateRangeButton, (newVal) => {
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const start = new Date(today);

  switch (newVal) {
    case "TODAY":
      start.setHours(0, 0, 0, 0);
      break;
    case "1M":
      start.setMonth(today.getMonth() - 1);
      start.setHours(0, 0, 0, 0);
      break;
    case "3M":
      start.setMonth(today.getMonth() - 3);
      start.setHours(0, 0, 0, 0);
      break;
    case "6M":
      start.setMonth(today.getMonth() - 6);
      start.setHours(0, 0, 0, 0);
      break;
    default:
      return;
  }

  startDate.value = toDateInputFormat(start);
  endDate.value = toDateInputFormat(today);
});

watch(
  () => route.query,
  (newQuery) => {
    const queryFilter = newQuery.filter || "";
    const queryStart = newQuery.startDate;
    const queryEnd = newQuery.endDate;

    orderStatusFilter.value = queryFilter;

    if (queryStart && queryEnd) {
      startDate.value = queryStart;
      endDate.value = queryEnd;
    }

    // watch 안에서는 래핑 함수 호출
    fetchOrders();
  }
);
</script>

<style lang="scss" scoped>
.consumer-mypage-order {
  padding: 36px 24px;
  margin: auto;
  max-width: 1200px;
  min-height: 70vh;

  > .main-container {
    display: flex;
    gap: 24px;

    > .menu-container {
      width: 256px;
      > div {
        &:not(:first-child) {
          margin-top: 36px;
        }
        > ul {
          margin-top: 16px;
          > li {
            color: #666;
            &:not(:first-child) {
              margin-top: 8px;
            }

            > button {
              border: none;
              background: none;
              color: #666;
              font-size: 16px;
              cursor: pointer;
            }
          }
        }
      }
    }

    > .order-container {
      flex: 1;
      > div {
        > .order-filter-container {
          margin-top: 16px;

          > select {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #999;
            border-radius: 4px;
            box-sizing: border-box;
            outline: none;
            font-size: 16px;
          }

          > .date-button-container {
            display: flex;
            margin-top: 8px;
            > label {
              flex: 1;
              border: 1px solid #999;
              padding: 8px 12px;
              text-align: center;
              cursor: pointer;
            }

            input:checked + label {
              border-color: black;
            }
          }

          > .date-range-container {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-top: 8px;

            > input {
              width: 100%;
              padding: 8px 12px;
              border: 1px solid #999;
              border-radius: 4px;
              box-sizing: border-box;
              outline: none;
              font-size: 16px;
            }
          }

          > button {
            font-size: 16px;
            border: none;
            background: black;
            font-weight: 700;
            width: 100%;
            color: white;
            margin-top: 8px;
            padding: 8px 12px;
            border-radius: 4px;
            cursor: pointer;
          }
        }

        > .order-list-container {
          margin-top: 24px;

          > div {
            border-top: 2px solid black;

            &:not(:first-child) {
              margin-top: 16px;
            }

            > .order-title-container {
              display: flex;
              align-items: center;
              justify-content: space-between;
              background: #efefef;
              padding: 12px 16px;

              > strong {
                font-size: 14px;
                > span {
                  font-size: 12px;
                  font-weight: 400;
                  color: #666;
                  margin-left: 4px;
                }
              }

              > a {
                display: flex;
                align-items: center;
                > p {
                  font-weight: 700;
                  color: #007bff;
                  font-size: 14px;
                }

                > span {
                  color: #007bff;
                }
              }
            }

            > .order-product-container {
              margin-top: 16px;
              > .desc-container {
                display: flex;
                gap: 16px;

                > img {
                  width: 144px;
                  height: 144px;
                  border-radius: 8px;
                  object-fit: cover;
                  border: 1px solid #efefef;
                  padding: 4px;
                }

                > div {
                  > .name {
                    font-weight: 700;
                    font-size: 18px;
                  }

                  > .price {
                    margin-top: 4px;
                  }

                  > .option {
                    margin-top: 16px;
                    color: #666;
                  }
                }
              }

              > hr {
                margin: 16px 0;
              }

              > .order-status-container {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 16px;
                > .order-total-container {
                  > p,
                  strong {
                    font-size: 14px;
                    text-align: end;
                    line-height: 24px;

                    > span {
                      font-weight: 700;
                      color: #007bff;
                    }
                  }
                }

                > hr {
                  margin-top: 16px;
                }
              }
            }
          }
        }

        > .order-empty-container {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 16px;
          height: 280px;
          border-bottom: 1px solid #efefef;

          > span {
            font-size: 96px;
            color: #999;
          }

          > p {
            color: #999;
            font-size: 18px;
            font-weight: 700;
          }
        }
      }
    }
  }
}
</style>
